import { IonContent, IonHeader, IonPage, IonTitle, IonToolbar } from '@ionic/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { t } from 'ttag';

import { Spinner } from '@components/Spinner';
import { ExportStatus } from '@custom-types/Export';
import RoutePath from '@custom-types/RoutePath';
import { usePrinter } from '@hooks/usePrinter';
import { CheckAction, CheckEntity } from '@pages/Check/Enums';
import { CheckError } from '@pages/Check/Error';
import { useCheckActionMutation } from '@pages/Check/queries';
import { generateRoute } from '@services/navigation-service';
import { getEventInfo } from '@state/event';
import { getKioskEnabled } from '@state/kiosk';
import {
  CustomPrintingErrorCode,
  getErrorCategory,
  PrinterErrorCategory,
  useFindBluetoothPrinters,
  usePrintBadgeMutation,
} from '~capacitor-plugins/BadgePrinter';

import { PrinterError as ErrorScreen } from './Error';
import { useExportBadgeMutation, useGetBadgeExport } from './queries';
import { SponsorImage } from './SponsorImage';
import { Success as SuccessScreen } from './Success';

const useBadgeExport = (
  eventId: string,
  checkInCode: string,
  onFinishedExporting: (url: string | undefined, error?: Error) => void,
  testPrint = false
) => {
  const {
    mutateAsync: startExport,
    isLoading: isLoadingExportMutation,
    error: exportMutationError,
    data: mutationData,
    reset: resetExportMutation,
  } = useExportBadgeMutation(eventId, checkInCode, testPrint);
  const exportId = mutationData?.data.id;
  const {
    isFetching: isFetchingExportQuery,
    error: exportQueryError,
    data: exportData,
    remove: resetExportQuery,
  } = useGetBadgeExport(eventId, exportId, onFinishedExporting);
  const isLoading =
    isLoadingExportMutation ||
    isFetchingExportQuery ||
    exportData?.data.state === ExportStatus.Pending;
  const reset = useCallback(() => {
    resetExportMutation();
    resetExportQuery();
  }, [resetExportMutation, resetExportQuery]);
  return {
    startExport,
    exportId,
    isLoading,
    error: exportMutationError || exportQueryError,
    data: exportData?.data.artifacts[0],
    reset,
  };
};

enum PrintFlowState {
  Unavailable = 'UNAVAILABLE',
  Idle = 'IDLE',
  CheckIn = 'CHECK_IN',
  Export = 'EXPORT',
  Print = 'PRINT',
  Done = 'DONE',
  Error = 'ERROR',
}

const useFlow = (eventId: string, checkinCode: string, checkIn: boolean, testPrint = false) => {
  const { selectedPrinter, isLoadingSettings, settings } = usePrinter();
  const {
    mutateAsync: checkInMutation,
    isLoading: isLoadingCheckIn,
    error: checkInError,
    reset: resetCheckIn,
  } = useCheckActionMutation(eventId);
  const {
    mutate: printMutation,
    isLoading: isLoadingPrint,
    error: printError,
    data: printData,
    reset: resetPrint,
  } = usePrintBadgeMutation(settings);
  const [caughtExportError, setCaughtExportError] = useState<Error | undefined>();
  // This callback chains the polling export query to the print mutation
  const onFinishedExporting = useCallback(
    (url: string | undefined, error?: Error) => {
      if (url) {
        printMutation({ badgeUrl: url });
      } else {
        setCaughtExportError(error);
      }
    },
    [printMutation]
  );
  const {
    startExport,
    isLoading: isLoadingExport,
    error: networkExportError,
    reset: resetExport,
  } = useBadgeExport(eventId, checkinCode, onFinishedExporting, testPrint);
  const exportError = caughtExportError || networkExportError;

  // Reset the queries and mutations
  const reset = useCallback(() => {
    resetCheckIn();
    resetExport();
    resetPrint();
  }, [resetCheckIn, resetExport, resetPrint]);

  // Start the flow by checking in (optional) and then starting the export. This will trigger a
  // polling query, which will use `onFinishedExporting` to trigger the print mutation.
  const startFlow = useCallback(async () => {
    reset();

    // We don't need to worry about throwing an error here, as the error logic below will already
    // have flagged this as an error. We just need to prevent the rest of the effect from running
    if (!selectedPrinter) return;

    if (checkIn) {
      await checkInMutation({
        action: CheckAction.CheckIn,
        person: { checkinCode },
        entityType: CheckEntity.Event,
        entityId: eventId,
      });
    }
    await startExport();
  }, [reset, selectedPrinter, checkIn, startExport, checkInMutation, checkinCode, eventId]);

  const checkInErrorCode =
    !isLoadingCheckIn && checkInError ? CustomPrintingErrorCode.CheckInFailed : undefined;
  const exportErrorCode =
    !isLoadingExport && exportError ? CustomPrintingErrorCode.UnableToGenerateBadge : undefined;
  // If the printer returned an error, we can use the error code from the error message, or fall
  // back to our default connection error
  const printErrorCode: CustomPrintingErrorCode | undefined =
    !isLoadingPrint && printError instanceof Error
      ? Number(printError.message) || CustomPrintingErrorCode.FailedToConnectToPrinter
      : undefined;
  const rawError = checkInError || caughtExportError || printError;
  let errorCode = checkInErrorCode || exportErrorCode || printErrorCode;

  // If there is no printer when this hook is called, it should immediately error out
  if (!selectedPrinter) {
    errorCode = CustomPrintingErrorCode.FailedToConnectToPrinter;
  }

  let state: PrintFlowState = PrintFlowState.Idle;
  if (errorCode) {
    state = PrintFlowState.Error;
  } else if (isLoadingSettings) {
    state = PrintFlowState.Unavailable;
  } else if (isLoadingCheckIn) {
    state = PrintFlowState.CheckIn;
  } else if (isLoadingExport) {
    state = PrintFlowState.Export;
  } else if (isLoadingPrint) {
    state = PrintFlowState.Print;
  } else if (printData?.result === true) {
    state = PrintFlowState.Done;
  }

  return { startFlow, state, rawError, errorCode };
};

const Content = styled.div`
  display: grid;
  grid-template-rows: 1fr;
  justify-items: center;
  align-items: center;
  height: 100%;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  justify-content: center;
`;

export interface PrintProps {
  checkInCode: string;
  shouldCheckIn?: string | boolean;
  fullPage?: boolean;
  testPrint?: boolean;
  dismiss: () => void;
}

export const Print = ({
  dismiss,
  checkInCode,
  shouldCheckIn = false,
  testPrint = false,
  fullPage = true,
}: PrintProps) => {
  const { id: eventId, name: eventName } = useSelector(getEventInfo);
  const { startFlow, state, errorCode } = useFlow(eventId, checkInCode, !!shouldCheckIn, testPrint);
  const { refetch: findBluetoothPrinters } = useFindBluetoothPrinters();
  const [tries, setTries] = useState(0);
  const isKiosk = useSelector(getKioskEnabled);

  const startFlowWithRetry = useCallback(async () => {
    setTries(curr => curr + 1);
    await findBluetoothPrinters();
    startFlow();
  }, [findBluetoothPrinters, startFlow]);

  useEffect(() => {
    startFlowWithRetry();
  }, [startFlowWithRetry]);

  if (errorCode) {
    const cancelRoute = isKiosk
      ? generateRoute(RoutePath.KioskCheck, { eventId })
      : generateRoute(RoutePath.EventDetail, { eventId });

    if (getErrorCategory(errorCode) === PrinterErrorCategory.CheckInFailed) {
      return <CheckError />;
    }

    return (
      <ErrorScreen
        errorCode={errorCode}
        tries={tries}
        retry={startFlowWithRetry}
        cancelRoute={cancelRoute}
      />
    );
  }

  if (state === PrintFlowState.Done) {
    return <SuccessScreen dismiss={dismiss} />;
  }

  const stateTitles: Record<PrintFlowState, string> = {
    [PrintFlowState.Unavailable]: t`Preparing to Print...`,
    [PrintFlowState.Idle]: t`Preparing to Print...`,
    [PrintFlowState.CheckIn]: t`Checking In...`,
    [PrintFlowState.Export]: t`Generating Badge...`,
    [PrintFlowState.Print]: t`Printing Badge...`,
    [PrintFlowState.Done]: t`Done`,
    [PrintFlowState.Error]: t`An Error Occurred`,
  };

  const content = (
    <IonContent>
      <Content>
        <Info>
          <Spinner />
          <h1>{stateTitles[state]}</h1>
          <p>{t`Please wait while we complete this process.`}</p>
        </Info>
        <SponsorImage />
      </Content>
    </IonContent>
  );

  if (!fullPage) {
    return content;
  }

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonTitle className="ion-text-wrap">{eventName}</IonTitle>
        </IonToolbar>
      </IonHeader>
      {content}
    </IonPage>
  );
};
